import LogoIcon from '@/app/components/common/LogoIcon';
import { headerHeightLg, headerHeightMd, headerHeightSm } from '@/constants/layouts';
import customTheme from '@/constants/theme.custom';
import { useSetNavbarHeight } from '@/queries/layout.queries';
import envService from '@/services/env.service';
import { Box, Flex, FlexProps, Grid, Image, Link } from '@cmpsr/components';
import { ReactNode, useCallback } from 'react';

type LogoNavigationAction = string;

type LogoClickAction = () => void;

type OnLogoClicked = LogoNavigationAction | LogoClickAction;

export type NavigationBarProps = {
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  onLogoClicked?: OnLogoClicked;
  disableLogoClick?: boolean;
  variant?: 'light' | 'dark' | 'light-minimal' | 'dark-minimal' | 'white';
  color?: string;
  beta?: boolean;
} & FlexProps;

export default function NavigationBar(props: NavigationBarProps): React.ReactElement {
  const { disableLogoClick, leftContent, rightContent, variant = 'dark', beta, ...restProps } = props;
  const setNavbarHeightMutation = useSetNavbarHeight();

  const navBarRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) setNavbarHeightMutation.mutateAsync(node.getBoundingClientRect().height);
  }, []);

  // Determine background based on variant
  const background =
    variant === 'white'
      ? 'white'
      : variant === 'light' || variant === 'light-minimal'
      ? customTheme.colors.tan
      : 'gradient-01';

  const lightVariant = variant === 'light' || variant === 'light-minimal' || variant === 'white';

  const betaText = props.beta ? (
    <Box as="span" color={lightVariant ? customTheme.colors.darkBlue : 'white'} fontSize="12px" fontWeight="400">
      beta
    </Box>
  ) : null;

  // Render the logo based on variant
  const renderLogo = () => {
    if (variant === 'light' || variant === 'white') {
      // For light/white variant, use the dark logo (icon + text)
      return (
        <Flex alignItems="center" gap="spacer-1">
          <LogoIcon alt="Catch logo icon" width={{ base: '24px', lg: '24px' }} height={{ base: '24px', lg: '24px' }} />
          <Image
            src="/images/dark-catch-logo-spelled.svg"
            width={{ base: '88px', lg: '100px' }}
            height={{ base: '21px', lg: '24px' }}
            alt="Catch logo text"
          />
          <Box mt={{ base: '-20px', lg: '-25px' }}>{betaText}</Box>
        </Flex>
      );
    } else if (variant === 'light-minimal' || variant === 'dark-minimal') {
      // For minimal variants, use just the LogoIcon
      const color = variant === 'light-minimal' ? customTheme.colors.darkBlue : 'white';
      return (
        <Flex alignItems="center">
          <LogoIcon
            alt="Catch logo icon"
            width={{ base: '20px', lg: '24px' }}
            height={{ base: '20px', lg: '24px' }}
            color={color}
          />
          <Box mt={{ base: '-20px', lg: '-25px' }}>{betaText}</Box>
        </Flex>
      );
    } else {
      // For dark variant, use the standard white logo
      return (
        <Flex alignItems="center">
          <Image
            src="/images/logo-catch.svg"
            width={{ base: '112px', lg: '140px' }}
            height={{ base: 'spacer-5', lg: '25px' }}
            alt="Catch logo"
          />
          <Box mt={{ base: '-20px', lg: '-25px' }}>{betaText}</Box>
        </Flex>
      );
    }
  };

  return (
    <Box as="nav" width="100%" background={background} px={{ base: 5, md: 8, lg: 16, xl: 24, xxl: 32 }} ref={navBarRef}>
      <Grid
        height={{ base: headerHeightSm, md: headerHeightMd, lg: headerHeightLg }}
        gridTemplateColumns="1fr 140px 1fr"
        justifyContent="center"
        alignItems="center"
        maxWidth="1184px"
        mx="auto"
        {...restProps}
      >
        {props.leftContent ? <Box>{props.leftContent}</Box> : <Box></Box>}
        {disableLogoClick ? (
          <Box justifySelf="center">{renderLogo()}</Box>
        ) : (
          <Link
            as="a"
            size="m"
            justifySelf="center"
            // eslint-disable-next-line no-restricted-syntax
            href={envService.getEnv().VITE_LANDING_APP_URL}
            aria-label="Catch logo"
          >
            {renderLogo()}
          </Link>
        )}

        {props.rightContent ? <Box textAlign="right">{props.rightContent}</Box> : null}
      </Grid>
    </Box>
  );
}
